import React, { useContext, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import ReactFlagsSelect from "react-flags-select";
import { CartContext } from "./CartContext";
import productData from "../Datas/productData";
import "../css/checkout.css";

const SHIPPING_FEE = 9.99;
// Whole-country destinations unavailable under our payment/shipping risk policy.
// Targeted sanctions against individuals or entities still require provider-side screening.
const RESTRICTED_COUNTRIES = ["BY", "CU", "IR", "KP", "RU", "SY"];

const createOrderNumber = () => {
  const digits = new Uint32Array(2);
  if (window.crypto?.getRandomValues) window.crypto.getRandomValues(digits);
  else {
    digits[0] = Math.floor(Math.random() * 1000000000);
    digits[1] = Math.floor(Math.random() * 1000000000);
  }
  return `${Date.now()}${String(digits[0] % 100000000).padStart(8, "0")}`;
};

const CheckOut = () => {
  const { cartItems } = useContext(CartContext);
  const [form, setForm] = useState({
    email: "",
    firstName: "",
    lastName: "",
    company: "",
    address: "",
    address2: "",
    city: "",
    postalCode: "",
  });
  const [countryCode, setCountryCode] = useState("");
  const [errors, setErrors] = useState({});

  const getProductById = (id) => productData.find((product) => product.id === id);
  const subtotal = useMemo(
    () => cartItems.reduce((total, item) => {
      const product = getProductById(item.id);
      return product ? total + Number(product.price) * item.quantity : total;
    }, 0),
    [cartItems]
  );
  const total = subtotal + (cartItems.length ? SHIPPING_FEE : 0);

  const updateField = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
    setErrors((current) => ({ ...current, [name]: "" }));
  };

  const validate = () => {
    const next = {};
    if (!/^\S+@\S+\.\S+$/.test(form.email)) next.email = "Enter a valid email address.";
    ["firstName", "lastName", "address", "city", "postalCode"].forEach((field) => {
      if (!form[field].trim()) next[field] = "This field is required.";
    });
    if (!countryCode) next.country = "Select a delivery country.";
    if (!cartItems.length) next.cart = "Your cart is empty.";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handlePlaceOrder = (event) => {
    event.preventDefault();
    if (!validate()) return;

    const firstItem = getProductById(cartItems[0].id);
    const origin = window.location.origin;
    const paymentUrl = new URL("/create_payment.php", origin);
    paymentUrl.search = new URLSearchParams({
      orderNo: createOrderNumber(),
      amount: total.toFixed(2),
      currency: "USD",
      email: form.email.trim(),
      success_url: `${origin}/payment?state=success`,
      cancel_url: `${origin}/payment?state=cancel`,
      title: firstItem.name,
    }).toString();
    window.location.assign(paymentUrl.toString());
  };

  return (
    <div className="main-containerCheck">
      <div className="check-custom-checkout-page">
        <div className="left">
          <div className="check-order-info">
            <form onSubmit={handlePlaceOrder} noValidate>
              <div className="form-group">
                <label htmlFor="email">Contact</label>
                <input type="email" id="email" name="email" value={form.email} placeholder="Email address" onChange={updateField} autoComplete="email" />
                {errors.email && <p className="error">{errors.email}</p>}
              </div>

              <div className="form-group">
                <label>Delivery</label>
                <ReactFlagsSelect
                  selected={countryCode}
                  onSelect={(code) => { setCountryCode(code); setErrors((current) => ({ ...current, country: "" })); }}
                  countries={RESTRICTED_COUNTRIES}
                  blacklistCountries
                  searchable
                  searchPlaceholder="Search countries"
                  placeholder="Select country"
                  className="check-select"
                />
                {errors.country && <p className="error country-error">{errors.country}</p>}
                <p className="checkout-help">Destinations unavailable under applicable payment or trade restrictions are excluded.</p>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <input name="firstName" value={form.firstName} placeholder="First name *" onChange={updateField} autoComplete="given-name" />
                  {errors.firstName && <p className="error">{errors.firstName}</p>}
                </div>
                <div className="form-group">
                  <input name="lastName" value={form.lastName} placeholder="Last name *" onChange={updateField} autoComplete="family-name" />
                  {errors.lastName && <p className="error">{errors.lastName}</p>}
                </div>
              </div>

              <div className="form-group">
                <input name="company" value={form.company} placeholder="Company name (optional)" onChange={updateField} autoComplete="organization" />
              </div>
              <div className="form-group">
                <input name="address" value={form.address} placeholder="Address *" onChange={updateField} autoComplete="address-line1" />
                {errors.address && <p className="error">{errors.address}</p>}
              </div>
              <div className="form-group">
                <input name="address2" value={form.address2} placeholder="Apartment, suite, etc. (optional)" onChange={updateField} autoComplete="address-line2" />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <input name="city" value={form.city} placeholder="City *" onChange={updateField} autoComplete="address-level2" />
                  {errors.city && <p className="error">{errors.city}</p>}
                </div>
                <div className="form-group">
                  <input name="postalCode" value={form.postalCode} placeholder="Postal code *" onChange={updateField} autoComplete="postal-code" />
                  {errors.postalCode && <p className="error">{errors.postalCode}</p>}
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="shippingMethod">Shipping method</label>
                <input id="shippingMethod" value="Standard tracked shipping — $9.99" readOnly />
              </div>

              <div className="payment-redirect-notice">
                <strong>Secure payment</strong>
                <p>After selecting Pay Now, you will continue to the payment provider. Card details are not entered or stored on this website.</p>
                <div className="checkout-payment-badges" aria-label="Supported payment methods">
                  <span>VISA</span><span>Mastercard</span><span>AMEX</span><span>Discover</span>
                </div>
              </div>

              {errors.cart && <p className="error cart-error">{errors.cart}</p>}
              <button className="check-checkout-button" type="submit" disabled={!cartItems.length}>Pay Now</button>
            </form>

            <p><Link to="/returns" style={{ textDecoration: "underline" }}>Shipping &amp; Returns Info</Link></p>
          </div>
        </div>

        <div className="right">
          <div className="check-order-summary">
            <div className="check-order-details">
              {cartItems.map((item, index) => {
                const product = getProductById(item.id);
                if (!product) return null;
                return (
                  <div key={`${item.id}-${item.size}-${item.color}-${index}`} className="check-cart-item">
                    <img src={product.image} alt={product.name} />
                    <div className="pro--qty"><div className="qty-inner">{item.quantity}</div></div>
                    <div className="check-item-details">
                      <h3>{product.name}<br />Size: {item.size}<br />Color: {item.color}</h3>
                    </div>
                    <p className="cal-single">${(Number(product.price) * item.quantity).toFixed(2)}</p>
                  </div>
                );
              })}

              <div className="check-summary-details">
                <div className="checkout-total-line"><span>Subtotal</span><span>${subtotal.toFixed(2)}</span></div>
                <div className="checkout-total-line"><span>Shipping</span><span>${cartItems.length ? SHIPPING_FEE.toFixed(2) : "0.00"}</span></div>
                <div className="checkout-total-line checkout-grand-total"><strong>Total USD</strong><strong>${total.toFixed(2)}</strong></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckOut;
